import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { skipToken, useMutation, useSuspenseQuery } from '@apollo/client/react';
import { Space } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatSettings, useCurrentChat } from '@guuru/chat-web';
import events from '@guuru/events-web';
import { emailHelper } from '@guuru/helpers-common';
import { SendButtonOutlined } from '@guuru/react-icon';
import ExpertFeedbackAvatar from '../../ExpertFeedbackAvatar';
import EmailInput from './EmailInput';
import ESCALATE_CHAT from './mutations/EscalateChat';
import RATE_CHAT from './mutations/RateChat';
import GET_BAD_RATE_INFO from './queries/GetBadRateInfo';
import {
  EndNoEmailWrapper,
  RateBadExpertEndNoMail,
  RateBadExpertEndNoMailUnderline,
  RateBadExpertWrapper,
  RateExpertBadTeaserBottom,
  RateExpertBadTeaserTop,
  RateExpertBadTeaserWrapper,
} from './styles';

const ChatRateBadExpert = function () {
  const { t } = useTranslation();
  const [escalateChat, { loading: isEscalating }] = useMutation(ESCALATE_CHAT);
  const [rateChat] = useMutation(RATE_CHAT);
  const { useTrackEventOnce, rateBadPrompted } = events;

  const { chatId } = useCurrentChat();
  const {
    data: {
      chatSettings: { name: partnerName, isEscalationsEnabled } = {},
    } = {},
  } = useChatSettings();

  const [emailValue, setEmailValue] = useState();
  const [validEmail, setValidEmail] = useState();

  const { data: { chat: { user, expert } = {} } = {} } = useSuspenseQuery(
    GET_BAD_RATE_INFO,
    chatId ? { variables: { id: chatId } } : skipToken,
  );

  useTrackEventOnce(() => rateBadPrompted(), [chatId]);

  useEffect(() => {
    // Fill controlled input when the visitor changes async
    // oxlint-disable-next-line react/set-state-in-effect
    setEmailValue(user?.email);
    // oxlint-disable-next-line react/set-state-in-effect
    setValidEmail(emailHelper.isValid(user?.email));
  }, [user]);

  const handleConfirmBadRateclick = useCallback(
    (rating) => {
      if (rating === 'no-email') {
        rateChat({ variables: { id: chatId, rating: 'bad' } });
      } else if (emailHelper.isValid(emailValue)) {
        escalateChat({
          variables: {
            id: chatId,
            visitorEmail: emailValue,
          },
        });
      }
    },
    [emailValue, rateChat, chatId, escalateChat],
  );

  const handleChange = useCallback(
    (event) => {
      const { value } = event.target;
      setEmailValue(value);
      setValidEmail(emailHelper.isValid(value));
    },
    [setEmailValue, setValidEmail],
  );

  const handlePressEnter = useCallback(() => {
    handleConfirmBadRateclick('with-email');
  }, [handleConfirmBadRateclick]);

  let Icon = validEmail ? SendButtonOutlined : CloseOutlined;
  Icon = isEscalating ? LoadingOutlined : Icon;

  return (
    <RateBadExpertWrapper aria-label="Bad rating">
      <RateExpertBadTeaserWrapper orientation="vertical">
        <Space orientation="vertical" style={{ height: 25 }}>
          <ExpertFeedbackAvatar expertPhoto={expert?.photoUrl} />
        </Space>
        <RateExpertBadTeaserTop>
          {t('chat:::rateExpertBadHeadline', { expertName: expert?.name })}
        </RateExpertBadTeaserTop>
        {isEscalationsEnabled && (
          <RateExpertBadTeaserBottom>
            {t('chat:::rateExpertBadTeaser', { partnerName })}
          </RateExpertBadTeaserBottom>
        )}
      </RateExpertBadTeaserWrapper>
      {isEscalationsEnabled && (
        <EmailInput
          autoFocus
          aria-label="Input your email here to be contacted later"
          enterButton={<Icon />}
          placeholder={t('chat:::placeholderEmail')}
          value={emailValue}
          onSearch={handlePressEnter}
          onChange={handleChange}
          type="email"
          style={{ fontFamily: 'inherit' }}
        />
      )}
      <EndNoEmailWrapper align="middle" justify="center" type="flex">
        <RateBadExpertEndNoMail>
          {isEscalationsEnabled && t('chat:::rateExpertBadEndWithoutEmail01')}{' '}
          <RateBadExpertEndNoMailUnderline
            onClick={() => {
              handleConfirmBadRateclick('no-email');
            }}
            onKeyPress={() => {
              handleConfirmBadRateclick('no-email');
            }}
            // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="button"
            tabIndex="0"
          >
            {t('chat:::rateExpertBadEndWithoutEmail02')}
          </RateBadExpertEndNoMailUnderline>
        </RateBadExpertEndNoMail>
      </EndNoEmailWrapper>
    </RateBadExpertWrapper>
  );
};

export default ChatRateBadExpert;
